import { useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  HouseNightlyTripIndividualFormContainer,
  HouseNightlyTripIndividualFormContainerProps,
} from './components/form-container/HouseNightlyTripIndividualFormContainer'
import { HouseNightlyTripIndividualIsEnabledField } from './components/is-enabled-field/HouseNightlyTripIndividualIsEnabledField'
import { HouseNightlyTripIndividualOptionsFields } from './components/options-fields/HouseNightlyTripIndividualOptionsFields'
import {
  computeHouseNightlyTripIndividualDefaultValues,
  computeHouseNightlyTripIndividualInputs,
} from './helpers/compute-house-nightly-trip-individual'
import { HouseNightlyTripIndividualDto } from '../../../../../../lib/graphql'
import { HouseOptionsFormLayoutProps } from '../../../HouseOptionsForm'

export type HouseNightlyTripIndividualFormProps = {
  updateNightlyTripIndividual: {
    mutate: (formData: HouseNightlyTripIndividualDto) => void
    isLoading: boolean
  }
}
export const HouseNightlyTripIndividualForm = ({
  handleOnExpand,
  house,
  isDisabled,
  isOpen,
  layoutProps,
  updateNightlyTripIndividual,
}: HouseNightlyTripIndividualFormProps & HouseOptionsFormLayoutProps) => {
  const [hasRequestBeenSent, setHasRequestBeenSent] = useState<boolean>(false)

  const { currencyArea, options } = house

  // Infer GQL Union type
  if (options.__typename !== 'HouseOptionsIndividual') {
    return null
  }

  const { nightlyTrip } = options

  const methods = useForm<HouseNightlyTripIndividualDto>({
    defaultValues: computeHouseNightlyTripIndividualDefaultValues(nightlyTrip),
  })

  const {
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
  } = methods

  const { mutate } = updateNightlyTripIndividual

  const isEnabled = watch('isEnabled')

  const timeOutRequest = () => {
    setHasRequestBeenSent(true)
    setTimeout(() => setHasRequestBeenSent(false), 2000)
  }

  const onSubmit = (formData: HouseNightlyTripIndividualDto) => {
    const inputs = computeHouseNightlyTripIndividualInputs({ ...formData })

    mutate({ ...inputs })

    reset(formData)

    timeOutRequest()
  }

  const isFieldDisabled = isDisabled || hasRequestBeenSent

  const formContainerProps: Omit<
    HouseNightlyTripIndividualFormContainerProps,
    'children'
  > = {
    handleOnExpand,
    isOpen,
    layoutProps,
    submitButtons: {
      confirm: hasRequestBeenSent,
      disabled: isFieldDisabled || !isDirty || !isValid,
      isLoading: isSubmitting || updateNightlyTripIndividual.isLoading,
      onSubmit: handleSubmit(onSubmit),
      isValid,
    },
  }

  return (
    <FormProvider {...methods}>
      <HouseNightlyTripIndividualFormContainer {...formContainerProps}>
        <HouseNightlyTripIndividualIsEnabledField
          control={control}
          isFieldDisabled={isFieldDisabled}
        />

        {isEnabled ? (
          <HouseNightlyTripIndividualOptionsFields
            control={control}
            currencyArea={currencyArea}
            isFieldDisabled={isFieldDisabled}
          />
        ) : null}
      </HouseNightlyTripIndividualFormContainer>
    </FormProvider>
  )
}
